import { Button, Clickable, Col, Footer, Icon, Logo, NotifBadge, Row, Text } from 'components';
import { isNonProfileApp } from 'core';
import { theme } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { AddButton, LogoutModal, Menu } from './components';

const HeaderItem = ({
  type,
  location,
  getIcon,
  dimensions = 20,
  navigate,
  isMobile,
  user,
  onClick
}) => {
  const selected =
    location === type ||
    (type === 'home' && location === '') ||
    (type === 'messages' && location.includes('messages')) ||
    location.includes(type);

  return (
    <Clickable
      marg={`0 0 0 ${isMobile ? 16 : 27}px`}
      onClick={onClick || (() => navigate(`/${type}`))}
    >
      <Row centerAll wid="24px" ht="24px">
        <Icon
          type={getIcon(type, selected)}
          col={selected ? '#000' : theme.GREY_50}
          dimensions={dimensions}
        />
      </Row>
    </Clickable>
  );
};

const Header = (props) => {
  const [menuVisible, setMenuVisible] = useState(),
    [logoutVisible, setLogoutVisible] = useState(false),
    { isMobile, showNotifications } = useSelector((state) => state.appState),
    user = useSelector((state) => state.user),
    chats = useSelector((state) => state.chats),
    navigate = useNavigate(),
    location = useLocation(),
    locationPath = location.pathname.slice(1),
    dispatch = useDispatch(),
    isApp = isNonProfileApp(location.pathname),
    isAuth = ['/signup', '/login'].includes(location.pathname),
    isAdmin = location.pathname.includes('/admin'),
    hasNotif = !!user?.notifCount,
    { t } = useTranslation(),
    hasMessageNotif = chats?.find((x) => {
      const foundUser = x?.users?.find((y) => y.user?._id === user?._id && y.notifCount);

      return foundUser && !foundUser.hidden;
    });

  function handleMenuVisible() {
    setMenuVisible(!menuVisible);
  }

  function handleLogoutVisible() {
    setLogoutVisible(!logoutVisible);
  }

  function handleNotifs() {
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: {
        showNotifications: !showNotifications
      }
    });
  }

  function handleGetApp() {
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: {
        showGetApp: true
      }
    });
  }

  function getIcon(label, selected) {
    return `${label}${selected ? 'Filled' : 'Outline'}`;
  }

  function handleLogin() {
    const currentPath = location.pathname + location.search;
    navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
  }

  function handleSignup() {
    const currentPath = location.pathname + location.search;
    navigate(`/signup?redirect=${encodeURIComponent(currentPath)}`);
  }

  if (isMobile && user && !isApp && !isAuth) return null;
  if (isAdmin) return null;

  if (user)
    return (
      <React.Fragment>
        <LogoutModal visible={logoutVisible} closeModal={handleLogoutVisible} navigate={navigate} />

        {(!isMobile || !locationPath.includes('profile') || locationPath.length < 9) && (
          <React.Fragment>
            <Col
              wid={isMobile ? '100vw' : '100%'}
              ht={isMobile ? '56px' : '72px'}
              pos="fixed"
              bg="#fff"
              top="0"
              centerAll
              borderBottom={`1px solid ${theme.GREY_20}`}
              zIndex={2}
            >
              <Row wid={isMobile ? '100%' : '936px'} ht={isMobile ? '56px' : '72px'} center between>
                <Row center wid="100%" pad={isMobile ? '0 16px' : '0'}>
                  <Logo wid="164px" isApp />

                  <Row center>
                    {isMobile && (
                      <React.Fragment>
                        <Clickable onClick={handleGetApp} marg="0 0 0 16px">
                          <Row centerAll wid="24px" ht="24px" relative>
                            <Icon type="addCircleOutline" col="#000" dimensions={20.5} />
                          </Row>
                        </Clickable>
                      </React.Fragment>
                    )}

                    {!isMobile && (
                      <React.Fragment>
                        <HeaderItem
                          type="home"
                          getIcon={getIcon}
                          navigate={navigate}
                          location={locationPath}
                          isMobile={isMobile}
                        />

                        {user.type === 'athlete' && (
                          <HeaderItem
                            type="dashboard"
                            getIcon={getIcon}
                            navigate={navigate}
                            location={locationPath}
                            isMobile={isMobile}
                            dimensions={18}
                          />
                        )}

                        <HeaderItem
                          type="browse"
                          getIcon={getIcon}
                          navigate={navigate}
                          location={locationPath}
                          isMobile={isMobile}
                          dimensions={22}
                        />

                        <HeaderItem
                          type="shoutout"
                          getIcon={getIcon}
                          navigate={navigate}
                          location={locationPath}
                          isMobile={isMobile}
                          dimensions={22}
                        />

                        <HeaderItem
                          type="store"
                          getIcon={getIcon}
                          navigate={navigate}
                          location={locationPath}
                          isMobile={isMobile}
                          dimensions={16}
                        />

                        <Col pos="relative" center>
                          <HeaderItem
                            type="profile"
                            getIcon={getIcon}
                            location={locationPath}
                            onClick={handleMenuVisible}
                            isMobile={isMobile}
                          />

                          <Menu
                            visible={menuVisible}
                            close={handleMenuVisible}
                            handleLogoutVisible={handleLogoutVisible}
                          />
                        </Col>
                      </React.Fragment>
                    )}

                    <Clickable marg={`0 0 0 ${isMobile ? 16 : 27}px`} onClick={handleNotifs}>
                      <Row centerAll wid="24px" ht="24px" pos="relative">
                        {!!hasNotif && <NotifBadge absolute />}

                        <Icon
                          type={getIcon('notification', showNotifications)}
                          col={isMobile || showNotifications ? '#000' : theme.GREY_50}
                          dimensions={20.5}
                        />
                      </Row>
                    </Clickable>

                    <Clickable
                      marg={`0 0 0 ${isMobile ? 16 : 27}px`}
                      onClick={() => navigate(`/messages`)}
                    >
                      <Row centerAll wid="24px" ht="24px" pos="relative">
                        {!!hasMessageNotif && <NotifBadge absolute />}

                        <Icon
                          type={getIcon('messages', locationPath === 'messages')}
                          col={locationPath === 'messages' || isMobile ? '#000' : theme.GREY_50}
                          dimensions={20}
                        />
                      </Row>
                    </Clickable>

                    {!isMobile && (
                      <Col marg="0 0 0 32px">
                        <AddButton />
                      </Col>
                    )}
                  </Row>
                </Row>
              </Row>
            </Col>

            <Col marg={`0 0 ${isMobile ? '56px' : '72px'}`}></Col>
          </React.Fragment>
        )}

        {isMobile && <Footer />}
      </React.Fragment>
    );

  return (
    <Col
      ht={isMobile ? '48px' : '72px'}
      noFlex
      wid="100vw"
      bg={isMobile ? '#000' : '#fff'}
      centerAll
      borderBottom={isMobile ? '1px solid #fff' : '0'}
    >
      <Row between center wid={isMobile ? 'calc(100vw - 32px)' : '936px'}>
        <Logo white={isMobile} wid={isMobile ? '121px' : '164px'} />

        <Row centerAll gap={isMobile ? '12px' : '24px'}>

          <Clickable onClick={handleLogin}>
            <Row wid="41px">
              <Text b2 bold col={isMobile ? '#fff' : '#000'}>
                {t('website.login')}
              </Text>
            </Row>
          </Clickable>

          {isMobile ? (
            <Button
              text={t('common.getApp')}
              onClick={handleGetApp}
              app
              small
              pad="0 12px 0 12px"
              bg={theme.SECONDARY}
            />
          ) : (
            <Button
              text={t('website.signUp')}
              app
              onClick={handleSignup}
              bg={theme.SECONDARY}
              pad="0 24px 0 24px"
              iconSize={22}
            />
          )}
        </Row>
      </Row>
    </Col>
  );
};

export default Header;
